import { Injectable } from '@angular/core';
import { Router, Event, NavigationStart, NavigationEnd } from '@angular/router';
import { scan, filter, observeOn } from 'rxjs/operators';
import { asyncScheduler } from 'rxjs';
import { ViewportScroller } from '@angular/common';

type Position = [number, number];

interface IScrollPositionRestore {
  event: Event;
  positions: { [K: number]: Position };
  trigger: 'imperative' | 'popstate' | 'hashchange';
  idToRestore: number;
}

@Injectable({
  providedIn: 'root'
})
export class RestoreScrollService {

  constructor(
    private router: Router,
    private viewportScroller: ViewportScroller
  ) { }

  watchAndRestore()
  {
    this.router.events.pipe(
      filter(ev => this.isNavigationStart(ev) || this.isNavigationEnd(ev)),
      scan<Event, IScrollPositionRestore>((acc: IScrollPositionRestore, ev: Event) => this.collect(acc, ev)),
      filter(({event, trigger}) => this.isNavigationEndWithTrigger(event, trigger)),
      observeOn(asyncScheduler)
    ).subscribe(({trigger, positions, idToRestore}) => {
      // if (trigger === 'imperative') this.scrollTo([0, 0]);
      if (trigger === 'popstate') this.scrollTo(positions[idToRestore] || [0, 0]);
    })
  }

  private collect(acc: IScrollPositionRestore, ev: Event): IScrollPositionRestore
  {
    const collected = {
      event: ev,

      positions: {
        ...acc.positions,
        ...(this.isNavigationStart(ev) ? {[ev.id]: this.getScrollTop()} : {})
      },

      trigger: this.isNavigationStart(ev) ? ev.navigationTrigger : acc.trigger,

      idToRestore: this.getIdToRestore(ev) || acc.idToRestore
    }

    return collected;
  }

  private isNavigationStart(ev: Event): ev is NavigationStart
  {
    return ev instanceof NavigationStart
  }

  private isNavigationEnd(ev: Event): ev is NavigationEnd
  {
    return ev instanceof NavigationEnd
  }

  private getScrollTop()
  {
    return this.viewportScroller.getScrollPosition();
  }

  private scrollTo(position: Position)
  {
    this.viewportScroller.scrollToPosition(position);
  }

  private getIdToRestore(ev: Event)
  {
    return this.isNavigationStart(ev) && ev.restoredState &&  ev.restoredState.navigationId + 1;
  }

  private isNavigationEndWithTrigger(event: Event, trigger: 'imperative' | 'popstate' | 'hashchange')
  {
    return this.isNavigationEnd(event) && !!trigger;
  }
}
